import * as THREE from "three";

/**
 * Meshes drawn ONLY into the sun's shadow map: reduced stand-ins for detailed casters (views/towerLod.ts), so the
 * shadow pass draws a quarter of a tower's triangles while the main pass keeps the full model.
 *
 * three r169 has no "shadow only" switch: layers do not do it (WebGLShadowMap tests the MAIN camera's layers) and a
 * material that writes nothing is still a draw in the main pass. What it does have is an order: `render` builds the
 * main pass's draw list first and renders the shadow map after. So a proxy stays `visible = false` — the main pass
 * never lists it — and is shown only for the duration of the shadow map's own render (`install`).
 */
export class ShadowProxies {
  private static readonly live = new Set<THREE.Object3D>();

  /** Wraps the renderer's shadow-map pass so the proxies are visible to it alone. Once per renderer. */
  static install(renderer: THREE.WebGLRenderer): void {
    const map = renderer.shadowMap;
    const render = map.render.bind(map);
    map.render = (lights, scene, camera) => {
      // the pass returns at once when nothing is due (manual updates): skip the toggling too
      if (!map.enabled || (!map.autoUpdate && !map.needsUpdate) || ShadowProxies.live.size === 0) { render(lights, scene, camera); return; }
      ShadowProxies.show(true);
      try { render(lights, scene, camera); } finally { ShadowProxies.show(false); }
    };
  }

  /** Registers a proxy (hidden from the main pass from now on). */
  static add(proxy: THREE.Object3D): void {
    proxy.visible = false;
    proxy.userData.shadowProxy = true;
    ShadowProxies.live.add(proxy);
  }

  /** Forgets every proxy under `root` (a model being thrown away; RigidRig.dispose calls it). */
  static removeUnder(root: THREE.Object3D): void {
    if (ShadowProxies.live.size === 0) return;
    root.traverse((o) => { if (o.userData.shadowProxy === true) ShadowProxies.live.delete(o); });
  }

  /** Proxies registered now (tests, the perf overlay). */
  static get count(): number { return ShadowProxies.live.size; }

  private static show(on: boolean): void { for (const p of ShadowProxies.live) p.visible = on; }
}
